import React, { useState, useEffect, useMemo } from "react";
import { getReporteSiscotelPendientes, getProveedoresDashboard, getFarmacia, reenviarNotificacionSiscotelManual } from "../../../helpers/backend_helper";
import { DatosSesion } from "../../../Components/Common/DatosSesion";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Badge,
  Input,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import TableContainer from "../../../Components/Common/TableContainer";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

// Formatear fechas
const formatearFecha = (fecha) => {
  if (!fecha) return "";
  return fecha instanceof Date
    ? fecha.toISOString().split("T")[0]
    : new Date(fecha).toISOString().split("T")[0];
};

// Obtener fecha actual y primer día del mes
const obtenerFechaActual = () => {
  const hoy = new Date();
  return formatearFecha(hoy);
};

const obtenerPrimerDiaMes = () => {
  const hoy = new Date();
  return formatearFecha(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
};

const ReporteSiscotelPendientes = () => {
  // Estados
  const [loading, setLoading] = useState(false);
  const [solicitudes, setSolicitudes] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(obtenerPrimerDiaMes());
  const [fechaFin, setFechaFin] = useState(obtenerFechaActual());
  const [codigoProveedor, setCodigoProveedor] = useState(null);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [cargandoProveedores, setCargandoProveedores] = useState(false);
  
  // Estados para selección y reenvío manual
  const [solicitudesSeleccionadas, setSolicitudesSeleccionadas] = useState(new Set());
  const [reenviando, setReenviando] = useState(false);
  const [modalConfirmacion, setModalConfirmacion] = useState(false);
  const [modalResultados, setModalResultados] = useState(false);
  const [resultadosReenvio, setResultadosReenvio] = useState(null);
  
  // Obtener perfil del usuario
  const perfil = DatosSesion("perfil") || "";
  const esAdministrador = perfil === "Administrador";
  const farmaciaId = DatosSesion("farmacia_id") || null;

  // Cargar proveedores solo si es administrador
  useEffect(() => {
    if (esAdministrador) {
      setCargandoProveedores(true);
      getProveedoresDashboard()
        .then((response) => {
          if (response.data?.data) {
            setProveedores(response.data.data);
          }
          setCargandoProveedores(false);
        })
        .catch((err) => {
          console.error("Error al cargar proveedores:", err);
          setCargandoProveedores(false);
        });
    } else if (farmaciaId) {
      // Si es farmacia, obtener su código de proveedor
      getFarmacia(farmaciaId)
        .then((response) => {
          if (response.data?.codigo_myh) {
            setCodigoProveedor(response.data.codigo_myh);
          }
        })
        .catch((err) => {
          console.error("Error al cargar farmacia:", err);
        });
    }
  }, [esAdministrador, farmaciaId]);

  // Opciones de proveedores para select nativo
  const proveedoresOptions = useMemo(() => {
    if (!proveedores || proveedores.length === 0) return [];
    return proveedores;
  }, [proveedores]);

  // Función para cargar datos
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (fechaInicio) {
        params.fecha_inicio = fechaInicio;
      }
      if (fechaFin) {
        params.fecha_fin = fechaFin;
      }
      if (codigoProveedor) {
        params.codigo_proveedor = codigoProveedor;
      }
      
      // Solo mostrar solicitudes de MyH+ (únicas que usan Siscotel)
      params.aseguradora = "MyH+";

      // Pasar params directamente, no como objeto anidado
      const response = await getReporteSiscotelPendientes(params);
      
      if (response && response.data) {
        setSolicitudes(response.data.data?.solicitudes || []);
        setEstadisticas(response.data.data?.estadisticas || null);
        
        const totalSolicitudes = response.data.data?.total_solicitudes || 0;
        const totalDeliveries = response.data.data?.total_deliveries_pendientes || 0;
        
        if (totalSolicitudes === 0) {
          toast.info("No se encontraron solicitudes MyH+ con deliveries pendientes para los filtros seleccionados");
        } else {
          toast.success(
            `Reporte cargado: ${totalSolicitudes} solicitudes MyH+, ${totalDeliveries} deliveries pendientes`
          );
        }
      }
    } catch (error) {
      console.error("Error al cargar el reporte:", error);
      toast.error("Error al cargar el reporte. Por favor, intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };
  
  // Manejar cambio de fechas
  const handleFechasChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setFechaInicio(formatearFecha(selectedDates[0]));
      setFechaFin(formatearFecha(selectedDates[1]));
    }
  };
  
  // Manejar cambio de proveedor
  const handleProveedorChange = (e) => {
    const nuevoProveedor = e.target.value === "" ? null : e.target.value;
    setCodigoProveedor(nuevoProveedor);
  };

  // Función para ver detalles
  const verDetalles = (solicitud) => {
    setSolicitudSeleccionada(solicitud);
    setModalDetalle(true);
  };

  // Funciones para selección múltiple
  const toggleSeleccionSolicitud = (identificador) => {
    console.log("🔍 toggleSeleccionSolicitud llamado con:", identificador);
    console.log("🔍 Estado actual solicitudesSeleccionadas:", Array.from(solicitudesSeleccionadas));
    
    const nuevasSeleccionadas = new Set(solicitudesSeleccionadas);
    if (nuevasSeleccionadas.has(identificador)) {
      nuevasSeleccionadas.delete(identificador);
      console.log("🔍 Deseleccionando:", identificador);
    } else {
      nuevasSeleccionadas.add(identificador);
      console.log("🔍 Seleccionando:", identificador);
    }
    
    console.log("🔍 Nuevo estado:", Array.from(nuevasSeleccionadas));
    setSolicitudesSeleccionadas(nuevasSeleccionadas);
  };

  const toggleSeleccionTodas = () => {
    console.log("🔍 toggleSeleccionTodas llamado");
    console.log("🔍 solicitudesSeleccionadas.size:", solicitudesSeleccionadas.size);
    console.log("🔍 solicitudes.length:", solicitudes.length);
    
    if (solicitudesSeleccionadas.size === solicitudes.length) {
      // Deseleccionar todas
      console.log("🔍 Deseleccionando todas");
      setSolicitudesSeleccionadas(new Set());
    } else {
      // Seleccionar todas
      console.log("🔍 Seleccionando todas");
      const todosIds = solicitudes.map(s => s.identificador);
      console.log("🔍 IDs a seleccionar:", todosIds);
      setSolicitudesSeleccionadas(new Set(todosIds));
    }
  };

  // Usar useMemo para evitar recálculos en cada render
  const todasSeleccionadas = useMemo(() => {
    return solicitudes.length > 0 && solicitudesSeleccionadas.size === solicitudes.length;
  }, [solicitudes.length, solicitudesSeleccionadas.size]);

  // Función para reenvío manual
  const reenviarSeleccionadas = async () => {
    if (solicitudesSeleccionadas.size === 0) {
      toast.warning("Por favor seleccione al menos una solicitud");
      return;
    }

    setModalConfirmacion(true);
  };

  const confirmarReenvio = async () => {
    setModalConfirmacion(false);
    setReenviando(true);

    try {
      const solicitudesParaReenviar = Array.from(solicitudesSeleccionadas).map(identificador => ({
        identificador
      }));

      const response = await reenviarNotificacionSiscotelManual(solicitudesParaReenviar);
      
      if (response.data?.status === "success") {
        setResultadosReenvio(response.data.estadisticas);
        setModalResultados(true);
        
        // Limpiar selección
        setSolicitudesSeleccionadas(new Set());
        
        // Recargar datos
        await cargarDatos();
        
        toast.success(`Reenvío completado: ${response.data.estadisticas.exitosas} exitosas, ${response.data.estadisticas.fallidas} fallidas`);
      } else {
        toast.error("Error al reenviar notificaciones");
      }
    } catch (error) {
      console.error("Error reenviando notificaciones:", error);
      toast.error(error.response?.data?.detail || "Error al reenviar notificaciones");
    } finally {
      setReenviando(false);
    }
  };

  const reenviarTodas = async () => {
    if (solicitudes.length === 0) {
      toast.warning("No hay solicitudes para reenviar");
      return;
    }

    setReenviando(true);

    try {
      const solicitudesParaReenviar = solicitudes.map(s => ({
        identificador: s.identificador
      }));

      const response = await reenviarNotificacionSiscotelManual(solicitudesParaReenviar);
      
      if (response.data?.status === "success") {
        setResultadosReenvio(response.data.estadisticas);
        setModalResultados(true);
        
        // Recargar datos
        await cargarDatos();
        
        toast.success(`Reenvío completado: ${response.data.estadisticas.exitosas} exitosas, ${response.data.estadisticas.fallidas} fallidas`);
      } else {
        toast.error("Error al reenviar notificaciones");
      }
    } catch (error) {
      console.error("Error reenviando notificaciones:", error);
      toast.error(error.response?.data?.detail || "Error al reenviar notificaciones");
    } finally {
      setReenviando(false);
    }
  };

  // Columnas de la tabla
  const columns = useMemo(
    () => [
      {
        id: "seleccion",
        header: () => (
          <Input
            type="checkbox"
            checked={todasSeleccionadas}
            onChange={toggleSeleccionTodas}
            className="form-check-input"
          />
        ),
        accessorKey: "identificador",
        cell: (info) => {
          const solicitud = info.row.original;
          return (
            <Input
              type="checkbox"
              checked={solicitudesSeleccionadas.has(solicitud.identificador)}
              onChange={() => toggleSeleccionSolicitud(solicitud.identificador)}
              className="form-check-input"
            />
          );
        },
        enableSorting: false,
        enableFiltering: false,
      },
      {
        id: "identificador",
        header: "Identificador",
        accessorKey: "identificador",
      },
      {
        id: "tipo",
        header: "Tipo",
        accessorKey: "tipo",
      },
      {
        id: "proveedor",
        header: "Proveedor",
        accessorKey: "codigo_proveedor",
      },
      {
        id: "titular",
        header: "Titular",
        accessorKey: "titular.nombre",
      },
      {
        id: "total_deliveries",
        header: "Total Deliveries",
        accessorKey: "total_deliveries",
      },
      {
        id: "deliveries_pendientes",
        header: "Pendientes",
        accessorKey: "deliveries_pendientes",
      },
      {
        id: "actualizado",
        header: "Última Actualización",
        accessorKey: "actualizado",
      },
      {
        id: "acciones",
        header: "Acciones",
        accessorKey: "identificador",
      },
    ],
    [solicitudesSeleccionadas, todasSeleccionadas] // Agregar dependencias para evitar stale closure
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-bottom-dashed">
                  <Row className="align-items-center">
                    <Col>
                      <h4 className="card-title mb-0">
                        <i className="ri-notification-3-line me-2"></i>
                        Notificaciones de Entregas Pendientes
                      </h4>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* Filtros */}
                  <Row className="mb-4 align-items-end">
                    {esAdministrador && (
                      <Col md={4}>
                        <Label className="form-label">Proveedor</Label>
                        <select
                          className="form-select"
                          value={codigoProveedor || ""}
                          onChange={handleProveedorChange}
                          disabled={cargandoProveedores}
                        >
                          <option value="">Todos los proveedores</option>
                          {proveedoresOptions.map((prov) => (
                            <option key={prov.codigo} value={prov.codigo}>
                              {prov.codigo} - {prov.nombre}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted d-block mt-1">
                          Filtrar por proveedor
                        </small>
                      </Col>
                    )}
                    <Col md={esAdministrador ? 6 : 8}>
                      <Label className="form-label">Rango de Fechas</Label>
                      <div className="input-group">
                        <div className="input-group-text bg-primary border-primary text-white">
                          <i className="ri-calendar-2-line"></i>
                        </div>
                        <Flatpickr
                          className="form-control"
                          options={{
                            mode: "range",
                            dateFormat: "d M, Y",
                            defaultDate: [
                              new Date(fechaInicio),
                              new Date(fechaFin),
                            ],
                            locale: {
                              firstDayOfWeek: 1,
                              weekdays: {
                                shorthand: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
                                longhand: [
                                  "Domingo",
                                  "Lunes",
                                  "Martes",
                                  "Miércoles",
                                  "Jueves",
                                  "Viernes",
                                  "Sábado",
                                ],
                              },
                              months: {
                                shorthand: [
                                  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
                                  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
                                ],
                                longhand: [
                                  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                                  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
                                ],
                              },
                            },
                          }}
                          onChange={handleFechasChange}
                          placeholder="Seleccionar rango de fechas"
                        />
                      </div>
                      <small className="text-muted d-block mt-1">
                        Seleccione el período a consultar
                      </small>
                    </Col>
                    <Col md={2}>
                      <Button
                        color="primary"
                        className="w-100"
                        onClick={cargarDatos}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Cargando...
                          </>
                        ) : (
                          <>
                            <i className="ri-search-line me-1"></i>
                            Buscar
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>

                  {/* Estadísticas */}
                  {estadisticas && (
                    <Row className="mb-4">
                      <Col md={3}>
                        <Card className="card-animate border border-primary">
                          <CardBody>
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <p className="text-uppercase fw-medium text-muted mb-0">
                                  Total Solicitudes
                                </p>
                              </div>
                              <div className="flex-shrink-0">
                                <h5 className="text-primary fs-14 mb-0">
                                  <i className="ri-file-list-3-line fs-3"></i>
                                </h5>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2">
                              <div>
                                <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                  {solicitudes.length}
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="card-animate border border-danger">
                          <CardBody>
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <p className="text-uppercase fw-medium text-muted mb-0">
                                  Deliveries Pendientes
                                </p>
                              </div>
                              <div className="flex-shrink-0">
                                <h5 className="text-danger fs-14 mb-0">
                                  <i className="ri-alarm-warning-line fs-3"></i>
                                </h5>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2">
                              <div>
                                <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-danger">
                                  {solicitudes.reduce(
                                    (acc, s) => acc + s.deliveries_pendientes,
                                    0
                                  )}
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="card-animate border border-info">
                          <CardBody>
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <p className="text-uppercase fw-medium text-muted mb-0">
                                  Agudas
                                </p>
                              </div>
                              <div className="flex-shrink-0">
                                <h5 className="text-info fs-14 mb-0">
                                  <i className="ri-pulse-line fs-3"></i>
                                </h5>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2">
                              <div>
                                <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                  {estadisticas.por_tipo?.Aguda || 0}
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                      <Col md={3}>
                        <Card className="card-animate border border-warning">
                          <CardBody>
                            <div className="d-flex align-items-center">
                              <div className="flex-grow-1">
                                <p className="text-uppercase fw-medium text-muted mb-0">
                                  Crónicas
                                </p>
                              </div>
                              <div className="flex-shrink-0">
                                <h5 className="text-warning fs-14 mb-0">
                                  <i className="ri-time-line fs-3"></i>
                                </h5>
                              </div>
                            </div>
                            <div className="d-flex align-items-end justify-content-between mt-2">
                              <div>
                                <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                  {estadisticas.por_tipo?.Cronica || 0}
                                </h4>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {/* Botones de Acción */}
                  {solicitudes.length > 0 && (
                    <Row className="mb-3">
                      <Col className="text-end">
                        <div className="d-inline-flex gap-2">
                          <Button
                            color="success"
                            onClick={reenviarSeleccionadas}
                            disabled={solicitudesSeleccionadas.size === 0 || reenviando}
                            className="btn-sm"
                          >
                            {reenviando ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1"></span>
                                Reenviando...
                              </>
                            ) : (
                              <>
                                <i className="ri-send-plane-line me-1"></i>
                                Reenviar Seleccionadas ({solicitudesSeleccionadas.size})
                              </>
                            )}
                          </Button>
                          <Button
                            color="primary"
                            onClick={reenviarTodas}
                            disabled={reenviando}
                            className="btn-sm"
                          >
                            {reenviando ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1"></span>
                                Reenviando...
                              </>
                            ) : (
                              <>
                                <i className="ri-send-plane-fill me-1"></i>
                                Reenviar Todas ({solicitudes.length})
                              </>
                            )}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  )}

                  {/* Tabla */}
                  {solicitudes.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={solicitudes}
                      isGlobalFilter={false}
                      customPageSize={10}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light text-muted"
                    />
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-inbox-line display-4 text-muted"></i>
                      <h5 className="mt-3">No hay datos para mostrar</h5>
                      <p className="text-muted">
                        Seleccione los filtros y haga clic en "Buscar" para cargar el reporte
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal de Detalles */}
      <Modal
        isOpen={modalDetalle}
        toggle={() => setModalDetalle(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setModalDetalle(false)}>
          Detalles de Deliveries Pendientes: {solicitudSeleccionada?.identificador}
        </ModalHeader>
        <ModalBody>
          {solicitudSeleccionada && (
            <>
              <div className="mb-3">
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Tipo:</strong>{" "}
                      <Badge color={solicitudSeleccionada.tipo === "Aguda" ? "info" : "warning"}>
                        {solicitudSeleccionada.tipo}
                      </Badge>
                    </p>
                    <p>
                      <strong>Proveedor:</strong> {solicitudSeleccionada.nombre_proveedor}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Titular:</strong> {solicitudSeleccionada.titular?.nombre}
                    </p>
                    <p>
                      <strong>Cédula:</strong> {solicitudSeleccionada.titular?.cedula}
                    </p>
                  </Col>
                </Row>
              </div>

              <h5 className="mb-3">Deliveries Pendientes de Notificación:</h5>
              <Table bordered hover responsive>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Estatus</th>
                    <th>Fecha Entrega</th>
                    <th>SISCOTEL Status</th>
                    <th>Intentos</th>
                    <th>Último Error</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitudSeleccionada.deliveries_detalle?.map((delivery) => (
                    <tr key={delivery.numero}>
                      <td>{delivery.numero}</td>
                      <td>
                        <Badge color="success">{delivery.estatus}</Badge>
                      </td>
                      <td>
                        {delivery.fecha_entrega
                          ? new Date(delivery.fecha_entrega).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>
                        <Badge
                          color={
                            delivery.siscotel_status === "failed"
                              ? "danger"
                              : delivery.siscotel_status === "pending"
                              ? "warning"
                              : "secondary"
                          }
                        >
                          {delivery.siscotel_status || "Sin notificar"}
                        </Badge>
                      </td>
                      <td>
                        <Badge color="info">{delivery.siscotel_intentos}</Badge>
                      </td>
                      <td>
                        <small
                          className="text-truncate d-block"
                          style={{ maxWidth: "200px" }}
                          title={delivery.siscotel_ultimo_error}
                        >
                          {delivery.siscotel_ultimo_error || "-"}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </ModalBody>
      </Modal>

      {/* Modal de Confirmación de Reenvío */}
      <Modal isOpen={modalConfirmacion} toggle={() => setModalConfirmacion(false)}>
        <ModalHeader toggle={() => setModalConfirmacion(false)}>
          <i className="ri-send-plane-line me-2"></i>
          Confirmar Reenvío de Notificaciones
        </ModalHeader>
        <ModalBody>
          <p>¿Está seguro que desea reenviar las notificaciones a Siscotel?</p>
          <div className="alert alert-info">
            <i className="ri-information-line me-2"></i>
            <strong>Información:</strong>
            <ul className="mb-0 mt-2">
              <li>Se reenviarán <strong>{solicitudesSeleccionadas.size}</strong> solicitudes seleccionadas</li>
              <li>Se ignorarán los límites de tiempo e intentos</li>
              <li>Esta acción se registrará para auditoría</li>
            </ul>
          </div>
        </ModalBody>
        <div className="modal-footer">
          <Button color="secondary" onClick={() => setModalConfirmacion(false)}>
            <i className="ri-close-line me-1"></i>
            Cancelar
          </Button>
          <Button color="primary" onClick={confirmarReenvio}>
            <i className="ri-send-plane-fill me-1"></i>
            Confirmar Reenvío
          </Button>
        </div>
      </Modal>

      {/* Modal de Resultados del Reenvío */}
      <Modal isOpen={modalResultados} toggle={() => setModalResultados(false)} size="lg">
        <ModalHeader toggle={() => setModalResultados(false)}>
          <i className="ri-check-line me-2"></i>
          Resultados del Reenvío
        </ModalHeader>
        <ModalBody>
          {resultadosReenvio && (
            <>
              {/* Resumen */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="border-success">
                    <CardBody className="text-center">
                      <h4 className="text-success mb-0">{resultadosReenvio.exitosas}</h4>
                      <small className="text-muted">Exitosas</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-danger">
                    <CardBody className="text-center">
                      <h4 className="text-danger mb-0">{resultadosReenvio.fallidas}</h4>
                      <small className="text-muted">Fallidas</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-warning">
                    <CardBody className="text-center">
                      <h4 className="text-warning mb-0">{resultadosReenvio.omitidas}</h4>
                      <small className="text-muted">Omitidas</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-info">
                    <CardBody className="text-center">
                      <h4 className="text-info mb-0">{resultadosReenvio.total_deliveries}</h4>
                      <small className="text-muted">Total Deliveries</small>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Detalles */}
              <h5 className="mb-3">Detalles por Solicitud:</h5>
              <div className="table-responsive">
                <Table bordered hover size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Solicitud</th>
                      <th>Delivery</th>
                      <th>Resultado</th>
                      <th>Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultadosReenvio.detalles.map((detalle, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{detalle.identificador}</strong>
                        </td>
                        <td>
                          #{detalle.delivery_numero || "-"}
                        </td>
                        <td>
                          <Badge
                            color={
                              detalle.success
                                ? "success"
                                : detalle.reason === "sin_deliveries_pendientes"
                                ? "warning"
                                : "danger"
                            }
                          >
                            {detalle.success ? "✓ Éxito" : "✗ Error"}
                          </Badge>
                        </td>
                        <td>
                          <small className={detalle.success ? "text-success" : "text-danger"}>
                            {detalle.message}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </ModalBody>
        <div className="modal-footer">
          <Button color="primary" onClick={() => setModalResultados(false)}>
            <i className="ri-check-line me-1"></i>
            Cerrar
          </Button>
        </div>
      </Modal>

      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default ReporteSiscotelPendientes;
